import { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Table, message } from "antd";
import {
  closePlaceholderTab,
  downloadPdfBlob,
  fetchOutboundArtifact,
  fetchOutboundFaxes,
  openPdfBlob,
  openPlaceholderTab,
} from "../../../service/fax";
import {
  SendLayout,
  SectionTitle,
  PrimaryBtn,
  FaxStatusPill,
  MetaText,
} from "../FaxStyled";

const PAGE_SIZE = 20;

/**
 * Outbound log for ONE shared fax line (the Intake tab): every fax sent
 * from that line by anyone, newest first. Opening the transmitted PDF
 * is limited to the sender and admins — the server says which rows via
 * `can_open_artifact`, so other rows show no Preview/Download links.
 */
const OutboundLineLogView = ({ line }) => {
  const [data, setData] = useState({ results: [], count: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  // Monotonic request sequence: a slower older response must never
  // overwrite a newer one (same guard as the other Fax page tables).
  const loadSeqRef = useRef(0);
  const artifactBusyRef = useRef(new Set());

  const load = useCallback(
    async (targetPage = 1) => {
      const seq = ++loadSeqRef.current;
      setLoading(true);
      try {
        const response = await fetchOutboundFaxes({
          page: targetPage,
          pageSize: PAGE_SIZE,
          line,
        });
        if (seq !== loadSeqRef.current) return;
        setData(response.data);
        setPage(targetPage);
      } catch {
        if (seq === loadSeqRef.current) {
          message.error("Could not load the outbound fax log.");
        }
      } finally {
        if (seq === loadSeqRef.current) setLoading(false);
      }
    },
    [line]
  );

  useEffect(() => {
    load(1);
  }, [load]);

  const handleArtifact = async (row, mode) => {
    if (artifactBusyRef.current.has(row.id)) return;
    artifactBusyRef.current.add(row.id);
    const tab = mode === "preview" ? openPlaceholderTab() : null;
    try {
      const response = await fetchOutboundArtifact(row.id);
      if (mode === "download") {
        downloadPdfBlob(response.data, row.document_name || `fax-${row.id}.pdf`);
      } else {
        openPdfBlob(response.data, tab);
      }
    } catch (error) {
      closePlaceholderTab(tab);
      const status = error?.response?.status;
      message.error(
        `Could not fetch the fax PDF${status ? ` (HTTP ${status})` : ""}.`
      );
    } finally {
      artifactBusyRef.current.delete(row.id);
    }
  };

  const columns = [
    {
      title: "Sent",
      dataIndex: "created_at",
      key: "created_at",
      width: 180,
      render: (value) => new Date(value).toLocaleString(),
    },
    { title: "To", dataIndex: "to_number", key: "to_number", width: 150 },
    {
      title: "Document",
      dataIndex: "document_name",
      key: "document_name",
      ellipsis: true,
      render: (value) => value || <MetaText>—</MetaText>,
    },
    {
      title: "Matter",
      dataIndex: "matter_name",
      key: "matter_name",
      width: 200,
      ellipsis: true,
      render: (value) => value || <MetaText>—</MetaText>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (value) => <FaxStatusPill tone={value}>{value}</FaxStatusPill>,
    },
    {
      title: "Pages",
      dataIndex: "num_pages",
      key: "num_pages",
      width: 80,
      render: (value) => value ?? <MetaText>—</MetaText>,
    },
    {
      title: "Sent By",
      dataIndex: "requested_by_name",
      key: "requested_by_name",
      width: 160,
      ellipsis: true,
      render: (value) => value || <MetaText>—</MetaText>,
    },
    {
      title: "",
      key: "actions",
      width: 170,
      render: (_, row) =>
        row.can_open_artifact ? (
          <span style={{ display: "inline-flex", gap: 12 }}>
            <a
              data-testid={`fax-line-artifact-preview-${row.id}`}
              onClick={(e) => {
                e.preventDefault();
                handleArtifact(row, "preview");
              }}
              href="#preview"
            >
              Preview
            </a>
            <a
              data-testid={`fax-line-artifact-download-${row.id}`}
              onClick={(e) => {
                e.preventDefault();
                handleArtifact(row, "download");
              }}
              href="#download"
            >
              Download
            </a>
          </span>
        ) : (
          <MetaText>—</MetaText>
        ),
    },
  ];

  return (
    <SendLayout>
      <SectionTitle>
        Sent from this line
        <PrimaryBtn
          style={{ padding: "6px 14px", fontSize: 13 }}
          onClick={() => load(page)}
          disabled={loading}
        >
          Refresh
        </PrimaryBtn>
      </SectionTitle>
      <Table
        style={{ marginTop: 12 }}
        size="middle"
        rowKey="id"
        columns={columns}
        dataSource={data.results}
        loading={loading}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: data.count,
          showSizeChanger: false,
          onChange: (nextPage) => load(nextPage),
        }}
      />
    </SendLayout>
  );
};

OutboundLineLogView.propTypes = {
  line: PropTypes.string.isRequired,
};

export default OutboundLineLogView;
